import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, Loader2, User as UserIcon } from "lucide-react";
import { apiClient, useUser } from "../lib/api-client";
import { useAccountUsers } from "../lib/hooks/useAccountUsers";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { CheckoutStatusBadge } from "../components/bookings/CheckoutStatusBadge";
import { ParticipantStatusBadge } from "../components/bookings/ParticipantStatusBadge";
import {
  formatDateTime,
  formatEventDate,
  formatPrice,
} from "../lib/utils";
import type { paths } from "../lib/api-types";

type UserParticipation =
  paths["/api/user/participations"]["get"]["responses"][200]["content"]["application/json"]["participations"][number];

export function Bookings() {
  const { data: user, isLoading: isUserLoading, isAuthenticated } = useUser();
  const accountUsers = useAccountUsers();
  const [participantFilter, setParticipantFilter] = useState<string>("all");

  const bookingsQuery = apiClient.useQuery(
    "get",
    "/api/user/bookings",
    {},
    { enabled: isAuthenticated },
  );

  const participationsQuery = apiClient.useQuery(
    "get",
    "/api/user/participations",
    {},
    { enabled: isAuthenticated },
  );

  const filteredParticipations = useMemo(() => {
    const participations = participationsQuery.data?.participations ?? [];
    if (participantFilter === "all") {
      return participations;
    }
    return participations.filter((p) => p.userId === participantFilter);
  }, [participationsQuery.data?.participations, participantFilter]);

  const { upcomingEvents, pastEvents } = useMemo(() => {
    const now = new Date();
    const upcoming = filteredParticipations
      .filter((p) => p.event.startsAt && new Date(p.event.startsAt) >= now)
      .sort(
        (a, b) =>
          new Date(a.event.startsAt ?? 0).getTime() -
          new Date(b.event.startsAt ?? 0).getTime(),
      );
    const past = filteredParticipations
      .filter((p) => !p.event.startsAt || new Date(p.event.startsAt) < now)
      .sort(
        (a, b) =>
          new Date(b.event.startsAt ?? 0).getTime() -
          new Date(a.event.startsAt ?? 0).getTime(),
      );
    return { upcomingEvents: upcoming, pastEvents: past };
  }, [filteredParticipations]);

  if (isUserLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto py-12 px-4 text-center">
        <div className="bg-white shadow sm:rounded-lg p-12">
          <UserIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Sign in to view your bookings
          </h3>
          <p className="text-gray-600 mb-6">
            Please sign in to see bookings and event participations for your
            account.
          </p>
          <div className="flex justify-center space-x-4">
            <Link
              to="/login"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
            >
              Sign In
            </Link>
            <Link
              to="/events"
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Back to Events
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const bookings = bookingsQuery.data?.bookings ?? [];
  const isLoading =
    bookingsQuery.isLoading || participationsQuery.isLoading;
  const hasError = bookingsQuery.isError || participationsQuery.isError;

  return (
    <div className="space-y-10">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Bookings & Events</h1>
        <p className="text-gray-600 mt-1">
          Bookings and event participations for everyone in your primary group
        </p>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      )}

      {hasError && !isLoading && (
        <Card>
          <CardContent className="py-12 text-center text-gray-600">
            Something went wrong loading your bookings. Please try again later.
          </CardContent>
        </Card>
      )}

      {!isLoading && !hasError && (
        <>
          <section>
            <Card>
              <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <CardTitle>Events</CardTitle>
                {accountUsers.length > 1 && (
                  <div className="flex items-center gap-2">
                    <label
                      htmlFor="participant-filter"
                      className="text-sm text-gray-600 whitespace-nowrap"
                    >
                      Filter by participant
                    </label>
                    <select
                      id="participant-filter"
                      value={participantFilter}
                      onChange={(e) => setParticipantFilter(e.target.value)}
                      className="block w-full sm:w-auto border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="all">All participants</option>
                      {accountUsers.map((accountUser) => (
                        <option key={accountUser.id} value={accountUser.id}>
                          {accountUser.firstName} {accountUser.lastName}
                          {accountUser.isSelf ? " (you)" : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-8">
                <ParticipationSection
                  title="Upcoming events"
                  participations={upcomingEvents}
                  emptyMessage="No upcoming event participations."
                />
                <ParticipationSection
                  title="Past events"
                  participations={pastEvents}
                  emptyMessage="No past event participations."
                />
              </CardContent>
            </Card>
          </section>

          <section>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="w-5 h-5" />
                  Bookings
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {bookings.length === 0 ? (
                  <p className="px-6 pb-6 text-gray-600">No bookings found.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Reference
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Booked by
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Events
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Submitted
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {bookings.map((booking) => (
                          <tr key={booking.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {booking.bookingReference ?? "—"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                              {booking.user
                                ? `${booking.user.firstName} ${booking.user.lastName}`
                                : "—"}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {booking.events.length === 0 ? (
                                "—"
                              ) : (
                                <ul className="space-y-1">
                                  {booking.events.map((event) => (
                                    <li key={event.id}>
                                      <Link
                                        to={`/events/${event.id}`}
                                        className="text-primary-600 hover:text-primary-800 hover:underline"
                                      >
                                        {event.title}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                              {booking.submittedAt
                                ? formatDateTime(booking.submittedAt)
                                : "—"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                              {formatPrice(booking.totalCents, booking.currency)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <CheckoutStatusBadge
                                status={booking.calculatedStatus}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </section>
        </>
      )}
    </div>
  );
}

type Participation = UserParticipation;

function ParticipationSection({
  title,
  participations,
  emptyMessage,
}: {
  title: string;
  participations: Participation[];
  emptyMessage: string;
}) {
  return (
    <div>
      <h3 className="text-base font-semibold text-gray-900 mb-3">{title}</h3>
      {participations.length === 0 ? (
        <p className="text-gray-600 text-sm">{emptyMessage}</p>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Event
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Participant
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Details
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {participations.map((participation) => (
                <tr key={participation.id}>
                  <td className="px-4 py-3 text-sm">
                    <Link
                      to={`/events/${participation.event.id}`}
                      className="font-medium text-primary-600 hover:text-primary-800 hover:underline"
                    >
                      {participation.event.title}
                    </Link>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                    {formatEventDate(participation.event.startsAt)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                    {participation.firstName} {participation.lastName}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {participation.event.eventKind === "RACE" &&
                    participation.boat ? (
                      <span>
                        {participation.boat.class} · Sail{" "}
                        {participation.boat.sailNumber}
                        {participation.pricePoint
                          ? ` · ${participation.pricePoint.label}`
                          : ""}
                      </span>
                    ) : participation.pricePoint ? (
                      participation.pricePoint.label
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <ParticipantStatusBadge status={participation.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
